<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { svgIcon, baseInput } from "@/common/components";

import { employeeList } from "@/common/api/rest/employee";

import type { EmployeeListType } from "@/common/types/employee";

const { t } = useI18n();

const emit = defineEmits(["update-data", "new:register"]);

const props = defineProps<{
  title?: string;
  accountId?: string | null;
  companyId?: string | null;
  evaluationId?: string | null;
  add?: boolean;
  unic?: boolean;
  ids?: string[];
}>();

const selecteds = ref<any[]>([]);
const listData = ref<EmployeeListType[]>([]);
const inputSearch = ref<string | null>("");

const searching = ref(false);

function searchEmployes() {
  if (!props.companyId) return;

  employeeList(props.accountId, props.companyId, {
    search: inputSearch.value as string,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        listData.value = data.list?.map((item: any) => ({
          employee_id: item.employee_id,
          name: item.name,
          role: item.role,
          registration_number: item.registration_number,
        }));
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function search() {
  searching.value = !!inputSearch.value;

  if (props.evaluationId) {
    searchEmployes();
    return;
  }

  searchEmployes();
}

function select(data: any) {
  if (props.ids?.includes(data.employee_id)) return;

  if (props.unic) {
    selecteds.value = [data];
  } else {
    if (selecteds.value.find((sl) => sl.employee_id === data.employee_id)) {
      selecteds.value = selecteds.value.filter(
        (sl) => sl.employee_id !== data.employee_id,
      );
    } else {
      selecteds.value.push(data);
    }
  }

  emit("update-data", selecteds.value);
}

function newEmployee() {
  emit("new:register", inputSearch.value);
}

onMounted(() => {
  search();
});
</script>

<template>
  <section class="employee-form">
    <div class="title">
      {{ title }}
    </div>
    <div class="search">
      <base-input
        v-model="inputSearch"
        background="#fff"
        color="#000"
        :placeholder="t('common.labels.search')"
        @key:enter="search()"
      >
        <template #sufix>
          <div class="icon">
            <svg-icon icon="search" size="1.75rem" @click="search()" />
          </div>
        </template>
      </base-input>
    </div>

    <div class="list">
      <template v-for="(item, index) in listData" :key="index">
        <div
          class="item"
          @click="select(item)"
          :class="{
            active: selecteds.find((sl) => sl.employee_id === item.employee_id),
            disabled: ids?.find((id) => id === item.employee_id),
          }"
        >
          <div class="name">{{ item.name }}</div>
          <div class="type">
            {{ item.role }}
          </div>
          <div class="type">
            {{ t("employee.labels.registration_number") }}:
            {{ item.registration_number }}
          </div>
        </div>
      </template>

      <template v-if="searching && listData.length === 0">
        <div class="empty">
          {{ t("common.texts.no_results") }}
        </div>
        <template v-if="add">
          <div class="box-add">
            <button
              class="btn btn-primary"
              :disabled="!companyId"
              @click="newEmployee()"
            >
              {{
                t("common.labels.new", {
                  concat: t("common.labels.registration"),
                })
              }}
            </button>
          </div>
        </template>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.employee-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    width: calc(100% - 0.5rem);
    height: 50dvh;
    overflow-y: scroll;
    padding-inline-end: 0.5rem;
    gap: 0.5rem;

    .item {
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      border: 1px solid var(--border);
      padding-block: 0.5rem;
      padding-inline: 1rem;
      border-radius: 0.5rem;

      &.active {
        border-color: var(--primary);
        background: var(--primary);
        color: var(--primary-color);
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .name {
        font-weight: bold;
      }
    }

    .empty {
      display: flex;
      flex-direction: column;
      text-align: center;
    }

    .box-add {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-block-start: 1.5rem;
    }
  }
}
</style>
